import moment from "moment";
import { useEffect, useState } from "react";
import { Container, Row, Table } from "react-bootstrap";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import setTitle from "../../../components/common/SetTitle";
import LoadingSpinner from "../../../components/loadingSpinner/LoadingSpinner";
import { OutletIProps } from "../../../components/sitebase/Base.types";
import InlineFeatureMap from "./components/InlineFeatureMap";
import { InlineMatchedFeature, InlineNotification } from "./Inline.types";
import { impactLevelName } from "./inlineImpact";
import { INLINE_MENU_LINKS } from "./InlineMenu";

import './InlineRules.css';

/** The feature properties worth showing, in the order they are shown. */
const FEATURE_PROPERTY_LABELS: { key: string, label: string }[] = [
    { key: "fid", label: "Feature ID" },
    { key: "riverName", label: "River" },
    { key: "basinName", label: "Basin" },
    { key: "countries", label: "Countries" },
    { key: "regions", label: "Regions" },
    { key: "upArea", label: "Upstream area" },
    { key: "impact_p90", label: "Impact (p90)" },
    { key: "sum_population_rounded", label: "Population affected" },
    { key: "sum_education", label: "Education facilities affected" },
    { key: "sum_health", label: "Health facilities affected" },
    { key: "sum_energy", label: "Energy facilities affected" },
];

const formatDate = (date: string | null) => date ? moment.utc(date).format("YYYY-MM-DD HH:mm") + " UTC" : "-";

/** The /inline routes render in the landing page layout, which has no side padding of its
 *  own, so the page adds its own to line up with the rest of the site. */
const PAGE_PADDING = "px-3 px-md-4 px-xl-5 py-3";

/**
 * The detail view of a single INLINE (EDERA) notification. It shows the matched
 * catchment feature on a map, the feature's own forecast properties, and the dates the
 * notification was issued/closed for.
 */
export default function InlineNotificationDetail() {
    const { notificationId } = useParams();
    const { setExtraMenuLinks } = useOutletContext<OutletIProps>();
    const [notification, setNotification] = useState<InlineNotification | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        setTitle("INLINE Notification Detail");
        setExtraMenuLinks(INLINE_MENU_LINKS, "Notifications");
    }, [setExtraMenuLinks]);

    useEffect(() => {
        if (!notificationId) {
            toast.error("Missing notification id.");
            setLoading(false);
            return;
        }

        setLoading(true);
        fetch(`/api/inline/notifications/${notificationId}/?format=json`, { credentials: "include" })
            .then(async (response) => {
                const data = await response.json().catch(() => null);
                if (!response.ok) {
                    const message = data?.message || data?.detail || JSON.stringify(data) || "Unknown error";
                    throw new Error(`HTTP ${response.status}: ${message}`);
                }
                return data as InlineNotification;
            })
            .then((data) => setNotification(data))
            .catch((error) => {
                console.error("Failed to load INLINE notification detail:", error);
                toast.error(`Failed to load the notification: ${error instanceof Error ? error.message : String(error)}`);
            })
            .finally(() => setLoading(false));
    }, [notificationId]);

    if (loading) {
        return <div className="mx-auto text-center py-5"><LoadingSpinner />Loading Notification</div>;
    }

    if (!notification) {
        return <Container fluid={true} className={PAGE_PADDING}><ToastContainer position="top-center" />
            <div className="py-5 text-center">This notification could not be loaded.</div>
        </Container>;
    }

    let feature: InlineMatchedFeature | null = null;
    try {
        feature = JSON.parse(notification.matched_feature) as InlineMatchedFeature;
    } catch (error) {
        console.warn("Failed to parse matched_feature", error);
    }

    const level = impactLevelName(feature?.properties?.impact_p90 as number | undefined);
    const properties = FEATURE_PROPERTY_LABELS
        .filter(({ key }) => feature?.properties?.[key] !== undefined && feature?.properties?.[key] !== null)
        .map(({ key, label }) => ({ label: label, value: String(feature!.properties[key]) }));

    return (
        <Container fluid={true} className={PAGE_PADDING} data-testid="inline-notification-detail">
            <ToastContainer position="top-center" />
            <Row className="py-3 my-2 border border-dashed">
                <div className="d-flex flex-row flex-wrap justify-content-between">
                    <span>
                        <strong>{notification.group || "No group"}</strong>
                        {notification.rule ?
                            <> - Rule: <Link to={`/inline/rules/${notification.rule}`}>{notification.rule_name || notification.rule}</Link></>
                            : null}
                        {" "}- Source ID: {notification.source_id}
                        {notification.historical ? " - Historical" : ""}
                    </span>
                    {level ? <span className={`rounded px-2 mx-2 edera-label-${level.toLowerCase()}`}>{level}</span> : null}
                </div>
                <div>
                    Issued on {formatDate(notification.issue_date)} from the forecast of {formatDate(notification.forecast_date)},
                    for a leadtime of {formatDate(notification.leadtime)}.
                    {notification.close_time ? ` Closed on ${formatDate(notification.close_time)}.` : " Still open."}
                </div>
            </Row>
            <Row className="border">
                {feature?.geometry ?
                    <InlineFeatureMap matched_features={[notification.matched_feature]} height="60vh" />
                    : <div className="py-5 text-center">This notification has no geometry to show on a map.</div>}
            </Row>
            <Row className="py-3 my-2">
                <span className="fw-bold mb-2">Matched feature</span>
                {properties.length === 0 ? <div>No feature properties available.</div> :
                    <Table className="table-hover border" data-testid="inline-notification-properties">
                        <tbody>
                            {properties.map((property) =>
                                <tr key={property.label}>
                                    <td className="fw-bold">{property.label}</td>
                                    <td>{property.value}</td>
                                </tr>)}
                        </tbody>
                    </Table>}
            </Row>
        </Container>
    );
}
